import { useState, useEffect, useLayoutEffect, useCallback } from "react";
import axios from "axios";
import { Search, ChevronLeft, ChevronRight, RefreshCw, Edit, Save, X, ArrowRightLeft, User, Calendar, CreditCard } from "lucide-react";
import { API_BASE } from "../config";

interface TransferDetail {
  _id: string;
  owner: string;
  member: string;
  amount: number;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const TransferDetails: React.FC = () => {
  const [transferData, setTransferData] = useState<TransferDetail[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useLayoutEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [search, pagination.limit]);

  const fetchTransferData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(pagination.page),
        limit: String(pagination.limit),
        searchTerm: search,
      });
      const response = await axios.get(`${API_BASE}/api/transfer/getTransferall?${params.toString()}`);

      if (response.data.status === "success" && response.data.Transfer) {
        setTransferData(response.data.Transfer);
        setPagination({
          page: response.data.page,
          limit: response.data.limit,
          total: response.data.total,
          totalPages: response.data.totalPages
        });
      }
    } catch (error) {
      console.error("Error fetching transfer data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, search]);

  useEffect(() => {
    fetchTransferData();
  }, [fetchTransferData]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleUpdateTransfer = async (index: number) => {
    const item = transferData[index];
    setIsUpdating(true);
    try {
      await axios.post(`${API_BASE}/api/transfer/admin/updateTransfer`, {
        owner: item.owner,
        member: item.member,
        amount: editValue,
        date: item.createdAt,
      });

      // Update the local state
      const updatedData = [...transferData];
      updatedData[index] = { ...item, amount: parseFloat(editValue) };
      setTransferData(updatedData);
      setEditIndex(null);
    } catch (error) {
      console.error("Error updating transfer:", error);
      alert("Failed to update transfer");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6 text-center md:text-left">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#523680] tracking-tight">Transfer Details</h1>
          <p className="mt-1 text-sm md:text-base text-gray-500 font-medium">Monitor and manage peer-to-peer wallet transfers</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchTransferData()}
            className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm group"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 text-gray-400 group-hover:text-blue-600 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Modern Filter Section */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-grow w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search Sender or Receiver..."
            className="w-full pl-11 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-[#523680]/20 transition-all text-gray-800 placeholder-gray-400 font-medium"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={pagination.limit}
            onChange={(e) => {
              setPagination((prev) => ({ ...prev, limit: parseInt(e.target.value, 10), page: 1 }));
            }}
            className="flex-grow sm:flex-none px-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-[#523680]/20 text-gray-700 font-bold text-sm"
          >
            {[10, 20, 50, 100].map(limit => (
              <option key={limit} value={limit}>{limit} / Page</option>
            ))}
          </select>
          <button
            onClick={() => fetchTransferData()}
            className="sm:hidden p-3 bg-gray-50 border-none rounded-xl text-[#523680]"
          >
            <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Transfer List */}
      <div className="grid grid-cols-1 gap-4">
        {isLoading && transferData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#523680] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : transferData.length > 0 ? (
          transferData.map((transfer, index) => (
            <div key={transfer._id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
              <div className="flex justify-between items-start mb-4">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Sender</p>
                  <h3 className="text-base font-bold text-[#523680] truncate pr-2">{transfer.owner}</h3>
                </div>
                <div className="px-2.5 py-1 bg-[#523680]/10 text-[#523680] rounded-full text-[10px] font-bold whitespace-nowrap border border-[#523680]/5">
                  Transfer
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-8 pt-4 border-t border-gray-50 items-center">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Receiver</p>
                  <p className="text-sm font-medium text-gray-600 truncate">{transfer.member}</p>
                </div>

                <div className="flex items-center justify-between sm:justify-start gap-4">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Amount</p>
                    {editIndex === index ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="number"
                          step="0.01"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-20 px-2 py-1 bg-white border border-[#523680] rounded font-bold text-[#523680] text-sm outline-none"
                          autoFocus
                        />
                        <button onClick={() => handleUpdateTransfer(index)} className="text-green-600 p-1 hover:bg-green-50 rounded" title="Save"><Save className="h-4 w-4" /></button>
                        <button onClick={() => setEditIndex(null)} className="text-red-500 p-1 hover:bg-red-50 rounded" title="Cancel"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-[#523680]">${(Number(transfer.amount) || 0).toFixed(2)}</span>
                        <button
                          onClick={() => {
                            setEditIndex(index);
                            setEditValue((transfer.amount || 0).toString());
                          }}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-all"
                          title="Edit Amount"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="min-w-0 sm:col-span-2 lg:col-span-1 border-t sm:border-t-0 sm:pt-0 pt-2 border-gray-50 flex justify-between items-end">
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Date</p>
                    <p className="text-sm font-medium text-gray-500">{new Date(transfer.createdAt).toLocaleDateString("en-GB")}</p>
                  </div>
                  <div className="text-[8px] font-mono text-gray-300 md:hidden">ID: {transfer._id.slice(-6)}</div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-gray-50 rounded-2xl border border-dashed border-gray-200 py-20 flex flex-col items-center justify-center">
            <p className="text-gray-400 font-medium">No transfer records found</p>
          </div>
        )}
      </div>

      {/* Normal Pagination (Light theme) */}
      {/* Pagination */}
      {!isLoading && transferData.length > 0 && (
        <div className="flex flex-col md:flex-row justify-between items-center mt-10 gap-6">
          <div className="text-xs font-bold text-gray-400 uppercase tracking-tight order-2 md:order-1">
            Page {pagination.page} / {pagination.totalPages}
          </div>
          <div className="flex space-x-2 order-1 md:order-2">
            <button
              className={`p-2 rounded-xl border transition-all ${pagination.page === 1 ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'hover:bg-white text-[#523680] border-[#523680]/20 shadow-sm'}`}
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Page numbers */}
            <div className="flex space-x-1.5 overflow-x-auto no-scrollbar max-w-[150px] sm:max-w-none px-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNumber;
                if (pagination.totalPages <= 5) pageNumber = i + 1;
                else if (pagination.page <= 3) pageNumber = i + 1;
                else if (pagination.page >= pagination.totalPages - 2) pageNumber = pagination.totalPages - 4 + i;
                else pageNumber = pagination.page - 2 + i;

                return (
                  <button
                    key={i}
                    className={`h-9 w-9 md:h-10 md:w-10 flex items-center justify-center rounded-xl font-bold text-xs md:text-sm transition-all shrink-0 ${pagination.page === pageNumber
                      ? 'bg-[#523680] text-white shadow-lg'
                      : 'border border-gray-100 hover:border-[#523680]/20 text-[#523680] bg-white'
                      }`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              className={`p-2 rounded-xl border transition-all ${pagination.page === pagination.totalPages ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed' : 'hover:bg-white text-[#523680] border-[#523680]/20 shadow-sm'}`}
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferDetails;
